import { createApp, reactive } from "petite-vue?module";
import SVGInject from "@iconfu/svg-inject";
import "@justinribeiro/lite-youtube";

import "./polyfills.js";
import "./$.js";
import "./rive.js";
import * as imports from "./imports.js";

const documentElement = document.documentElement;

const store = reactive({
  hash: location.hash,
  get searchParams() {
    return new URL(location).searchParams;
  },
  scroll: {
    top: window.pageYOffset || documentElement.scrollTop,
    direction: null,
  },
});

// Some of our exports can be functional components.
const components = {};
for (const [name, value] of Object.entries(imports)) {
  if (value instanceof Function) {
    components[name] = value;

    // Auto set mounted and unmounted handler
    $$(`[data-v-scope^="Query"]`).forEach((component) => {
      component.setAttribute("v-on:vue:mounted", "mounted");
      component.setAttribute("v-on:vue:unmounted", "unmounted");
    });
  }
}

// A Vue directive `v-intersect` to execute expression when the element
// enters or leaves the viewport.
const intersect = (ctx) => {
  const {
    el, // the element the directive is on
    exp, // the raw value expression, e.g. v-intersect="x" -> "x"
    arg = "enter", // v-intersect:leave -> "leave"
    modifiers = {}
  } = ctx;

  // Clean up custom attributes.
  for (const { name, value } of el.attributes) {
    if (!name.startsWith("v-intersect")) continue;
    if (value !== exp) continue;
    el.removeAttribute(name);
  }
  
  const modifierKeys = Object.keys(modifiers);

  let threshold = 0.0, rootMargin = "";

  /**
   * Placements modifiers
   * 
   * `v-intersect.top-bottom` => when top element meets half bottom of viewport
   * `v-intersect.top-center` => when top element meets center of viewport
   * `v-intersect.top-top` => when top element meets half top of viewport
   * `v-intersect.bottom-bottom` => when bottom element meets half bottom of viewport
   * `v-intersect.bottom-center` => when bottom element meets center of viewport
   * `v-intersect.bottom-top` => when bottom element meets half top of viewport
   * `v-intersect.center-bottom` => when center element meets half bottom of viewport
   * `v-intersect.center-center` => when center element meets center of viewport
   * `v-intersect.center-top` => when center element meets half top of viewport
   */
  const {
    "top-bottom": topBottom,
    "top-center": topCenter,
    "top-top": topTop,
    "center-bottom": centerBottom,
    "center-center": centerCenter,
    "center-top": centerTop,
    "bottom-bottom": bottomBottom,
    "bottom-center": bottomCenter,
    "bottom-top": bottomTop,
  } = modifiers;

  if (topBottom || topCenter || topTop) {
    threshold = 0;
  } else if (centerBottom || centerCenter || centerTop) {
    threshold = 0.5;
  } else if (bottomBottom || bottomCenter || bottomTop) {
    threshold = 1.0;
  }

  if (topTop || bottomTop || centerTop) {
    rootMargin = `0px 0px -50% 0px`;
  } else if (topCenter || bottomCenter || centerCenter) {
    rootMargin = `-50% 0px`;
  } else if (topBottom || bottomBottom || centerBottom) {
    rootMargin = `-50% 0px 0px 0px`;
  }

  /**
   * Threshold modifiers
   * 
   * `v-intersect.half` => 0.5
   * `v-intersect.full` => 1.0
   * `v-intersect.threshold.25` => 0.25
   * `v-intersect.threshold.100` => 1.0
   * `v-intersect.threshold.0` => 0.0
   */
  if (modifiers.half) {
    threshold = 0.5;
  } else if (modifiers.full) {
    threshold = 1.0;
  } else if (modifiers.threshold) {
    threshold = modifiers[modifierKeys.indexOf("threshold") + 1];
    if (threshold === "100") threshold = 1.0;
    if (threshold === "0") threshold = 0.0;
    threshold = Number(`.${threshold}`);
  }

  /**
   * Root margin modifiers
   * 
   * `v-intersect.margin.10px.20px.30px.40px`
   * `v-intersect.margin.-10px.-20px.-30px.-40px`
   * `v-intersect.margin.0`
   */
  if (modifiers.margin) {
    const index = modifierKeys.indexOf("margin");
    // Grab the 4 subsequent length values after it: x-intersect.margin.300px.0.50%.0
    const values = [];
    for (let i = 1; i < 5; i++) {
      const value = modifierKeys[index + i] || "";
      // Supported: -10px, -20 (implied px), 30 (implied px), 40px, 50%.
      // Also supports "percent" as a unit for % for XML parsers.
      const match = value.match(/^(-?[0-9]+)(px|%|percent)?$/);
      if (match) {
        let unit = match[2] || "px";
        if (unit === "percent") unit = "%";
        values.push(match[1] + unit);
      }
    }

    if (values.length) {
      rootMargin = values.join(" ");
    }
  }

  const options = {
    root: modifiers.parent ? el.parentElement : null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // evaluate the expression and get its value
        (arg === "enter") && ctx.get();
      } else {
        (arg === "leave") && ctx.get();
      }

      if (modifiers.once) {
        observer.disconnect();
      }
    });
  }, options);

  observer.observe(el);

  return () => {
    // cleanup if the element is unmounted
    observer.disconnect();
  }
}

// same as vue 2
const simplePathRE =
  /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*|\['[^']*?']|\["[^"]*?"]|\[\d+]|\[[A-Za-z_$][\w$]*])*$/

// A Vue directiive `v-scroll` for document scroll events.
const scroll = (ctx) => {
  const {
    el, // the element the directive is on
    exp, // the raw value expression, e.g. v-scroll="x" -> "x"
    arg = "", // v-scroll:end -> "end"
    modifiers: {
      once = false, //  v-scroll.once -> { once: true }
      ...modifiers
    } = {},
    get,
  } = ctx;

  let handler = simplePathRE.test(exp)
    ? get(`(e => ${exp}(e))`)
    : get(`($event => { ${exp} })`)

  let lastKnownScrollPosition = 0;
  let ticking = false;

  const scrollHandler = (event) => {
    lastKnownScrollPosition = window.scrollY;

    if (!ticking) {
      // Throttle the event to "do something" every 20ms
      setTimeout(() => {
        handler(event);
        ticking = false;

        if (once) {
          document.removeEventListener("scroll", scrollHandler);
        }
      }, 20);

      ticking = true;
    }
  }
  document.addEventListener("scroll", scrollHandler);

  const scrollEndHandler = (event) => {
    handler(event);
    if (once) {
      document.removeEventListener("scrollend", scrollEndHandler);
    }
  }

  if (arg === "end") {
    document.addEventListener("scrollend", scrollEndHandler.bind);
  }

  return () => {
    // cleanup if the element is unmounted
    document.removeEventListener("scroll", scrollHandler);
    document.removeEventListener("scrollend", scrollEndHandler);
  }
}

// Converts any data-v-* attributes to v-* attributes
function convertAttributes(root = document) {
  const evaluator = new XPathEvaluator();
  const snapshot = evaluator.evaluate('//*/@*[starts-with(local-name(), "data-v-")]', root, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
  for (let i=0; i < snapshot.snapshotLength; i++ ) {
    const { name, value, ownerElement } = snapshot.snapshotItem(i);
    let newName = name.replace("data-v-", "v-");
    if (name.startsWith("data-v-on-")) {
      newName = name.replace("data-v-on-", "v-on:");
    } else if (name.startsWith("data-v-bind-")) {
      newName = name.replace("data-v-bind-", "v-bind:");
    } else if (name.startsWith("data-v-intersect-")) {
      newName = name.replace("data-v-intersect-", "v-intersect:");
    } else if (name.startsWith("data-v-intersect.")) {
      newName = name.replace("data-v-intersect.", "v-intersect.");
    } else if (name.startsWith("data-v-scroll-")) {
      newName = name.replace("data-v-scroll-", "v-scroll:");
    } else if (name.startsWith("data-v-scroll")) {
      newName = name.replace("data-v-scroll", "v-scroll");
    }
    ownerElement.setAttribute(newName, value);
    ownerElement.removeAttribute(name);

    if (ownerElement.tagName.toLowerCase() === "template") {
      for (const child of ownerElement.content.children) {
        convertAttributes(child);
      }
    }
  };
}

convertAttributes();

if ($(`[v-scope]`)) {
  createApp({
    ...components,
    store,
    $delimiters: ["${", "}"],
  })
  .directive("intersect", intersect)
  .directive("scroll", scroll)
  .mount();
}

window.addEventListener("hashchange", () => {
  store.hash = location.hash;
});

window.addEventListener("scroll", function() {
  const scroll = store.scroll;
  const scrollTop = window.pageYOffset || documentElement.scrollTop;
  if (scrollTop > scroll.top && scroll.direction !== "bottom") {
    scroll.direction = "bottom";
  } else if (scrollTop < scroll.top) {
    scroll.direction = "top";
  }

  scroll.top = scrollTop <= 0 ? 0 : scrollTop; // for Mobile or negative scrolling
}, false);

// Track if the user has interacted with scroll via click, i.e. from anchor link.
const scrollendHandler = function() {
  // Resets after the next scroll event
  document.addEventListener("scroll", scrollHandler, {
    once: true,
    passive: true,
  });
};
const scrollHandler = function() {
  documentElement.dataset.userInteracted = "false";
};
document.addEventListener("click", function() {
  // Cleans up previous handlers if any
  document.removeEventListener("scroll", scrollHandler);
  document.removeEventListener("scrollend", scrollendHandler);

  documentElement.dataset.userInteracted = "true";
  // Waits until any browser scroll finishes
  document.addEventListener("scrollend", scrollendHandler, {
    once: true,
    passive: true,
  });
}, { 
  once: false,
  passive: true,
});

//#region SVG Injection
SVGInject.setOptions({
  onFail(img, svg) {
    // if injection fails show the img element
    img.classList.remove("injectable");
  },
});

document.addEventListener("DOMContentLoaded", function() {
  const svgs = $$(`img.injectable[src$=".svg"]`);
  SVGInject(svgs, {
    onAllFinish() {
      // the SVG injection has finished for all images
    }
  });
});
//#endregion SVG Injection

// Wraps any text nodes in paragraphs with link of the chunks with
// `<span>` so the arrow link styles are not applied to inline links.
document.querySelectorAll(`main > * p:has(>a:only-child)`).forEach((element) => {
  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const span = document.createElement("span");
      span.textContent = node.textContent;
      node.parentElement.insertBefore(span, node);
      node.remove();
    }
  }
});
